import { i18n } from "../i18n/i18n";

import React, { Component } from 'react'
import { compact, findIndex, includes, map, pullAt, reject, some } from 'lodash'
import { produce } from 'immer'
import confirmService from '../services/confirm.service'
import { ConfirmParams } from '../components/Confirm/Confirm.state'
import locationService from '../services/location.service'
import workOrderService from '../services/workOrder.service'
import palletService from '../services/pallet.service'
import geolocationService from '../services/geolocation.service'
import alertService from '../services/alert.service'
import locationApi, { LocationData } from '../apis/location.api'
import workOrderApi, { WorkOrderData } from '../apis/workOrder.api'
import palletApi, { PalletData } from '../apis/pallet.api'
import transferApi, { TransferCreateRequest } from '../apis/transfer.api'
import { ActionName } from '../components/Actions'
import Card from '../components/Card'
import CardButton from '../components/CardButton'
import ContentLayout from '../components/ContentLayout'
import ContentMain from '../components/ContentMain'
import InputText from '../components/InputText'
import PageHeading from '../components/PageHeading'
import Row from '../components/Row/Row'
import Select from '../components/Select'
import TableHorizontal, {
  TableHorizontalData, TableHorizontalHeader
} from '../components/TableHorizontal'
import SearchPicker, { SearchPickerOption } from '../components/SearchPicker'
import InputScan from '../components/InputScan'

interface TransferProps {}

interface TransferState {
  code: string
  location: string
  bin: string
  workOrder: string
  workOrderId: number | null
  latitude: number | null
  longitude: number | null
  pallets: TableHorizontalData<PalletData>[]
  locations: LocationData[]
  workOrders: WorkOrderData[]
}

export default class Transfer extends Component<TransferProps, TransferState> {
  tableHeaders: TableHorizontalHeader[] = [
    { heading: i18n("Pallet"), path: 'code' },
    { heading: i18n("Item code"), path: 'item.code' },
    { heading: i18n("Description"), path: 'item.description' },
    { heading: i18n("Current location"), path: 'aliveTransfer.location.name' },
    { heading: i18n("Bin"), path: 'aliveTransfer.bin' },
    { heading: i18n("Quantity"), path: 'quantity' },
    { heading: i18n("Actions") }
  ]

  constructor (props: TransferProps) {
    super(props)
    this.state = {
      code: '',
      location: '',
      bin: '0',
      workOrder: '',
      workOrderId: null,
      latitude: null,
      longitude: null,
      pallets: [],
      locations: [],
      workOrders: []
    }
    this.handleActionClick = this.handleActionClick.bind(this)
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleInputScan = this.handleInputScan.bind(this)
    this.handleWorkOrderSearch = this.handleWorkOrderSearch.bind(this)
    this.handleWorkOrderSelect = this.handleWorkOrderSelect.bind(this)
    this.handleWorkOrderBlur = this.handleWorkOrderBlur.bind(this)
    this.handleTransferClick = this.handleTransferClick.bind(this)
  }

  get locationSelectOptions () {
    return locationService.selectOptions(this.state.locations)
  }

  get location () {
    const { locations, location } = this.state
    if (!location) return
    return locationService.find(locations, Number(location))
  }

  get workOrderOptions () {
    return workOrderService.searchOptions(this.state.workOrders)
  }

  handleActionClick (id: number, action: ActionName) {
    if (action === 'delete') {
      const confirm: ConfirmParams = {
        id,
        type: 'warning',
        question: i18n('Are you sure to delete the pallet?'),
        confirmText: i18n('Yes'),
        cancelText: i18n('No'),
        onConfirm: () => this.handleDeleteConfirm(id)
      }
      confirmService.push(confirm)
    }
  }

  deletePallet (id: number) {
    this.setState(state => {
      return produce(state, draft => {
        const index = findIndex(draft.pallets, { id })
        pullAt(draft.pallets, index)
      })
    })
  }

  handleDeleteConfirm (id: number) {
    this.deletePallet(id)
  }

  readPallet (code: string) {
    palletApi.readByCode(code, pallet => {
      if (!pallet) return
      const isExists = palletService.isExists(this.state.pallets, pallet.id)
      if (isExists) {
        this.setState({ code: '' })
        return
      }
      const actions: ActionName[] = ['delete']
      const palletData:TableHorizontalData<PalletData> = pallet
      palletData.actions = actions
      this.setState(state => {
        return produce(state, draft => {
          draft.pallets.unshift(palletData)
          draft.code = ''
        })
      })
    })
  }

  handleInputChange (name: string, value: string) {
    if (name === 'code') {
      this.setState({ code: value })
    } else if (name === 'location') this.setState({ location: value })
    else if (name === 'bin') this.setState({ bin: value })
    else if (name === 'workOrder') {
      this.setState({ workOrderId: null, workOrder: value })
    }
  }

  handleInputScan (name: string, value: string) {
    if (name === 'code') this.readPallet(value)
  }

  handleWorkOrderSearch (name: string, code: string) {
    workOrderApi.searchByCode(code, workOrders => {
      if (!workOrders) return
      this.setState({ workOrders })
    })
  }

  handleWorkOrderSelect (name: string, option: SearchPickerOption) {
    const workOrderId = option.id as number
    this.setState({
      workOrder: option.value,
      workOrderId,
      workOrders: []
    })
  }

  handleWorkOrderBlur (name: string) {
    this.setState({ workOrders: [] })
  }

  resetState () {
    this.setState({
      code: '',
      location: '',
      bin: '0',
      workOrder: '',
      workOrderId: null,
      pallets: []
    })
  }

  handleTransferConfirm () {
    const { pallets, location, bin, workOrderId } = this.state
    const { latitude, longitude } = this.state
    const locationData = this.location
    const isProductionLocation = locationData && locationData.type && locationData.type.key === 'production'
    const payload: TransferCreateRequest = {}
    if (location) payload.location = Number(location)
    if (bin) payload.bin = bin
    if (workOrderId && isProductionLocation) payload.workOrder = workOrderId
    if (latitude) payload.latitude = latitude
    if (longitude) payload.longitude = longitude
    const transferRequests = map(pallets, pallet => {
      return new Promise((resolve, reject) => {
        payload.pallet = pallet.id
        transferApi.create(payload, transfer => {
          if (!transfer) reject(transfer)
          else resolve(transfer)
        })
      })
    })
    Promise.allSettled(transferRequests)
      .then(settlements => {
        const transfers = compact(map(settlements, 'value'))
        this.setState(state => {
          return produce(state, state => {
            const transferedPalletIds = map(transfers, 'palletId')
            state.pallets = reject(state.pallets, pallet => {
              return includes(transferedPalletIds, pallet.id)
            })
          })
        })
        const failedTransfers = some(settlements, { reason: null })
        if (!failedTransfers) {
          alertService.success('Successfully transferred')
          this.resetState()
        }
      })
  }

  handleTransferClick () {
    const pallet = this.state.pallets[0]
    if (!pallet) return
    const palletId = pallet.id
    const confirm: ConfirmParams = {
      id: palletId,
      question: i18n('Are you sure to transfer the pallets?'),
      confirmText: i18n('Yes'),
      cancelText: i18n('No'),
      onConfirm: () => this.handleTransferConfirm()
    }
    confirmService.push(confirm)
  }

  get workOrder () {
    const location = this.location
    if (!location || !location.type) return
    return (location.type.key === 'production') &&
      <SearchPicker
        name="workOrder"
        value={this.state.workOrder}
        options={this.workOrderOptions}
        label={i18n("Work order")}
        onSearch={this.handleWorkOrderSearch}
        onChange={this.handleInputChange}
        onSelect={this.handleWorkOrderSelect}
        onBlur={this.handleWorkOrderBlur} />
  }

  listLocations () {
    locationApi.list(locations => {
      if (!locations) return
      this.setState({ locations })
    })
  }

  readGeolocation () {
    geolocationService.access(coordinate => {
      if (!coordinate) return
      const { latitude, longitude } = coordinate
      this.setState({ latitude, longitude })
    })
  }

  componentDidMount () {
    this.listLocations()
    this.readGeolocation()
  }

  render () {
    return (
      <ContentLayout>
        <ContentMain layout="twoThird">
          <Card>
            <PageHeading
              text={i18n("Transfer")}
              doc='pallets-pallets-transfer-to-a-location' />
            <Row column={1}>
              <InputScan
                name="code"
                value={this.state.code}
                label={i18n("Scan pallet code")}
                autofocus={true}
                onChange={this.handleInputChange}
                onScan={this.handleInputScan} />
              <TableHorizontal
                headers={this.tableHeaders}
                data={this.state.pallets}
                onActionClick={this.handleActionClick} />
            </Row>
            <Row column={2}>
              <Select
                name="location"
                value={this.state.location}
                options={this.locationSelectOptions}
                label={i18n("Location")}
                onChange={this.handleInputChange} />
              <InputText
                name="bin"
                value={this.state.bin}
                label={i18n("Bin")}
                onChange={this.handleInputChange} />
              {this.workOrder}
            </Row>
            <CardButton
              proceedText={i18n("Transfer")}
              onProceedClick={this.handleTransferClick} />
          </Card>
        </ContentMain>
      </ContentLayout>
    )
  }
}
